import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ChevronDown } from "lucide-react";
import { resourceData } from "../data"; // Import the separated data
import { useNavigate } from "react-router-dom";

const Resources = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("All Resources");
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(6);

  const filters = ["All Resources", "Whitepaper", "Blogs"];

  const filteredResources = useMemo(() => {
    return resourceData.filter((item) => {
      const matchesFilter =
        activeFilter === "All Resources" ||
        item.type.toLowerCase() === activeFilter.toLowerCase();
      const matchesSearch = item.title
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  }, [activeFilter, searchQuery]);

  const handleLoadMore = () => setVisibleCount((prev) => prev + 6);

  return (
    <>
      <Helmet>
        <title>
          Cybersecurity Resources, Blogs & Whitepapers | Synthera Security
        </title>

        <meta
          name="description"
          content="Explore cybersecurity blogs, whitepapers, industry insights, Zero Trust security guidance, AI security, PAM, SASE, and threat intelligence resources from Synthera Security."
        />

        <meta
          name="keywords"
          content="Cybersecurity Resources, Cybersecurity Blogs, Whitepapers, PAM, Zero Trust, SASE, AI Security, Threat Intelligence, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Cybersecurity Resources, Blogs & Whitepapers | Synthera Security"
        />

        <meta
          property="og:description"
          content="Explore cybersecurity blogs, whitepapers, and expert security insights from Synthera Security."
        />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/resources"
        />

        <meta property="og:type" content="website" />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/resources"
        />
      </Helmet>

      <div className="min-h-screen bg-[#032d28] text-white font-sans pb-20 pt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-10">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Resources
            </h1>
          </div>

          {/* Featured Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl overflow-hidden shadow-2xl mb-16 flex flex-col md:flex-row relative"
          >
            <div className="p-8 md:p-12 md:w-1/2 flex flex-col justify-center items-start text-gray-900 z-10">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                Featured
              </span>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 leading-tight">
                Find out who is named Leaders in the 2025 Gartner® Magic for
                SASE
              </h2>
              <p className="text-gray-600 mb-8 text-sm md:text-base">
                Not all SASE is created equal. Download the report to see the
                leaders.
              </p>
              <a
                href="https://www.gartner.com/doc/reprints?id=00ThR000008ArWWUA0&ct=260730&st=sb"
                target="_blank"
                rel="noopener noreferrer"
              >
                <button className="bg-[#FDB913] hover:bg-[#e5a810] text-gray-900 font-bold py-3 px-8 rounded-full transition-colors shadow-md uppercase text-xs tracking-widest">
                  Get the Report
                </button>
              </a>
            </div>
            <div className="md:w-1/2 bg-white relative p-8 flex items-center justify-center overflow-hidden">
              <img
                src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1766817415/Cato-MQ-630x400-1.png_yvejih.webp"
                alt="Report"
                className="w-full h-full object-cover rounded-sm z-10 hover:scale-105 transition-transform duration-500 cursor-pointer"
              />
            </div>
          </motion.div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row justify-between items-end mb-10 gap-6 border-b border-gray-700/50 pb-8">
            <div className="relative w-full md:w-1/3">
              <label className="text-xs text-gray-400 mb-2 block uppercase tracking-widest font-semibold">
                Filter by Type
              </label>
              <select
                className="w-full appearance-none bg-[#0B251F] border border-gray-600 text-white py-3.5 pl-4 pr-10 rounded-xl focus:outline-none focus:border-[#FDB913] cursor-pointer"
                onChange={(e) => {
                  setActiveFilter(e.target.value);
                  setVisibleCount(6);
                }}
                value={activeFilter}
              >
                {filters.map((f, i) => (
                  <option key={i} value={f}>
                    {f}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-11 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            <div className="w-full md:w-1/3 relative">
              <input
                type="text"
                placeholder="Search by title..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setVisibleCount(4);
                }}
                className="w-full bg-transparent border-b border-gray-500 text-white py-3 pl-2 pr-10 focus:outline-none focus:border-[#FDB913] placeholder-gray-500 transition-colors"
              />
              <Search className="absolute right-2 bottom-3.5 w-5 h-5 text-gray-500" />
            </div>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {" "}
            <AnimatePresence mode="popLayout">
              {filteredResources.slice(0, visibleCount).map((resource) => (
                <motion.div
                  key={resource.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg group flex flex-col h-full hover:shadow-2xl transition-all duration-300 w-full max-w-md"
                >
                  <div
                    className={`h-64 relative p-6 flex flex-col justify-center overflow-hidden ${!resource.imageUrl ? `bg-gradient-to-br from-emerald-900 to-[#032d28]` : ""}`}
                  >
                    {resource.imageUrl ? (
                      <img
                        src={resource.imageUrl}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        alt=""
                      />
                    ) : null}
                    <div className="absolute inset-0 bg-black/20"></div>
                    <div className="relative z-10 flex justify-center items-center h-full group-hover:scale-110 transition-transform duration-500">
                      {resource.icon}
                    </div>
                  </div>
                  <div className="p-8 min-h-[250px] flex flex-col flex-grow items-center text-center text-gray-900">
                    <span className="inline-block px-3 py-1 rounded-full border border-teal-600 text-teal-700 text-[10px] font-bold uppercase mb-4 tracking-widest">
                      {resource.type}
                    </span>
                    <h3 className="font-bold text-xl mb-6 flex-grow leading-snug">
                      {resource.title}
                    </h3>
                    {/* FIXED YELLOW BUTTON FOR ALL CARDS */}
                    <button
                      onClick={() => {
                        // If it's a blog and has a blogUrl, route directly to it
                        if (resource.type === "Blogs" && resource.blogUrl) {
                          navigate(resource.blogUrl);
                        } else {
                          // Otherwise, go to the landing page (for Whitepapers, etc.)
                          navigate(`/resources/${resource.slug}`);
                        }
                      }}
                      className="bg-[#FDB913] hover:bg-[#032d28] hover:text-white text-[#032d28] text-[11px] font-black py-4 px-10 rounded-full transition-all duration-300 uppercase tracking-[0.2em] shadow-lg shadow-yellow-500/20 active:scale-95"
                    >
                      {resource.linkText || "Read More"}
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* --- LOAD MORE SECTION --- */}
          {visibleCount < filteredResources.length && (
            <div className="flex justify-center mt-16">
              <button
                onClick={handleLoadMore}
                className="px-12 py-4 border-2 border-gray-600 text-gray-300 hover:border-[#FDB913] hover:text-[#FDB913] rounded-full transition-all text-xs font-black uppercase tracking-[0.2em] active:scale-95"
              >
                Load More
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Resources;
